import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, CaretDown, Check, X, Package, Wrench } from "@phosphor-icons/react";

/**
 * Searchable picker for Products / Services / Other. Emits the same
 * `type:id` value the current Shadcn Select emitted (or the sentinel
 * `noneValue`) so drop-in swaps stay one-line.
 *
 * Usage:
 *   <SearchableItemSelect
 *     value={ln.item_id ? `${ln.item_type}:${ln.item_id}` : "none"}
 *     onChange={(v) => pickLineItem(i, v)}
 *     products={products}
 *     services={services}
 *     noneValue="none"
 *     noneLabel="Other / General work"
 *     placeholder="Choose product or service"
 *     testid={`pw-line-${i}-item`}
 *     className="h-8"
 *   />
 */
export default function SearchableItemSelect({
  value,
  onChange,
  products = [],
  services = [],
  noneValue = "none",
  noneLabel = "Other / General work",
  placeholder = "Choose product / service / other",
  testid = "item-picker",
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
    else setQ("");
  }, [open]);

  // Flatten products + services into a single option list with kind tag
  const all = useMemo(() => {
    const list = [];
    (products || []).forEach((p) => list.push({ id: p.id, name: p.name, hsn: p.hsn || "", kind: "product", raw: p }));
    (services || []).forEach((s) => list.push({ id: s.id, name: s.name, hsn: s.sac || s.hsn || "", kind: "service", raw: s }));
    return list;
  }, [products, services]);

  const selected = useMemo(() => {
    if (!value || value === noneValue) return null;
    const [type, id] = String(value).split(":");
    return all.find((x) => x.kind === type && x.id === id) || null;
  }, [value, all, noneValue]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return all;
    return all.filter((x) => `${x.name} ${x.hsn} ${x.kind}`.toLowerCase().includes(s));
  }, [all, q]);

  const pick = (opt) => {
    onChange?.(opt ? `${opt.kind}:${opt.id}` : noneValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={
            "flex h-8 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-2.5 py-1 text-[12.5px] shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 " +
            className
          }
        >
          <span className={selected ? "truncate" : "text-muted-foreground truncate"}>
            {selected ? (
              <>
                <span className="text-muted-foreground uppercase text-[10px] mr-1">{selected.kind}</span>
                {selected.name}
              </>
            ) : (
              value && value !== noneValue ? placeholder : noneLabel
            )}
          </span>
          <CaretDown size={12} className="opacity-60 shrink-0 ml-1" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px] max-w-[440px]"
        data-testid={`${testid}-popover`}
      >
        <div className="p-2 border-b border-border/60 sticky top-0 bg-background z-10">
          <div className="relative">
            <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${all.length} product${all.length === 1 ? "" : "s"} & service${services.length === 1 ? "" : "s"}…`}
              className="pl-7 pr-7 h-8 text-[13px]"
              data-testid={`${testid}-search`}
            />
            {q && (
              <button type="button" onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Clear search">
                <X size={12} />
              </button>
            )}
          </div>
        </div>
        <div className="max-h-[280px] overflow-y-auto py-1">
          <button
            type="button"
            onClick={() => pick(null)}
            className={`w-full text-left px-3 py-2 text-[13px] hover:bg-accent flex items-center gap-2 border-b border-border/40 ${
              !value || value === noneValue ? "bg-accent/60" : ""
            }`}
            data-testid={`${testid}-none`}
          >
            <span className="flex-1 text-muted-foreground italic">{noneLabel}</span>
            {(!value || value === noneValue) && <Check size={13} weight="bold" className="text-primary" />}
          </button>
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">
              No match for "<b>{q}</b>". Use "{noneLabel}" above and type a custom name.
            </div>
          ) : (
            filtered.map((opt) => {
              const isSel = value === `${opt.kind}:${opt.id}`;
              return (
                <button
                  type="button"
                  key={`${opt.kind}-${opt.id}`}
                  onClick={() => pick(opt)}
                  className={`w-full text-left px-3 py-2 text-[13px] hover:bg-accent flex items-center gap-2 ${
                    isSel ? "bg-accent/60" : ""
                  }`}
                  data-testid={`${testid}-option-${opt.kind}-${opt.id}`}
                >
                  {opt.kind === "product" ? (
                    <Package size={12} className="text-muted-foreground shrink-0" />
                  ) : (
                    <Wrench size={12} className="text-muted-foreground shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{opt.name}</div>
                    {opt.hsn && (
                      <div className="text-[10.5px] text-muted-foreground truncate font-mono">
                        {opt.kind === "service" ? "SAC" : "HSN"} · {opt.hsn}
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{opt.kind}</span>
                  {isSel && <Check size={13} weight="bold" className="text-primary shrink-0" />}
                </button>
              );
            })
          )}
        </div>
        {filtered.length > 0 && (
          <div className="px-3 py-1.5 text-[10.5px] text-muted-foreground border-t border-border/40 bg-muted/30 font-mono">
            {filtered.length} of {all.length} shown
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
